import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import LanguageModal from '../components/LanguageModal';
import { useAuth } from '../contexts/AuthContext';
import SeoHead from '../components/SeoHead';
import { MUSIC_SEO } from '../seo/seoConfig';
import { techArticleJsonLd } from '../seo/jsonLd';
import ga from '../services/analytics';
import { API_CONFIG } from '../config/api';
import { buildMcpConfigSnippet } from '../services/apiTokenService';
import {
  MCP_CREATOR_TOOLS,
  MCP_ENV_VARS,
  MCP_MUSIC_TOOLS,
  MCP_NPM_URL,
  MCP_PACKAGE_NAME,
  MCP_PLACEHOLDER_TOKEN,
  MCP_REPO_URL,
  MCP_SCOPES,
  MCP_SHARED_TOOLS,
} from '../content/mcpDocsContent';
import { SketchPageShell, SketchNavBar, SketchSection, SketchButton } from '../components/sketchy';

type McpDocsPageProps = {
  mcpProduct?: 'music' | 'creator' | 'both';
  showMusicTools?: boolean;
  showCreatorTools?: boolean;
};

const ToolTable = ({
  tools,
  nameLabel,
  descLabel,
}: {
  tools: { name: string; description: string }[];
  nameLabel: string;
  descLabel: string;
}) => (
  <div className="overflow-x-auto px-4 sm:px-6 pb-4">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="text-left border-b border-[var(--color-border)]">
          <th className="py-2 pr-4 font-semibold">{nameLabel}</th>
          <th className="py-2 font-semibold">{descLabel}</th>
        </tr>
      </thead>
      <tbody>
        {tools.map((tool) => (
          <tr key={tool.name} className="border-b border-[var(--color-border)]/50 align-top">
            <td className="py-2 pr-4 font-mono text-xs whitespace-nowrap">{tool.name}</td>
            <td className="py-2 text-[var(--color-muted)]">{tool.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const McpDocsPage = ({ mcpProduct = 'both', showMusicTools = true, showCreatorTools = true }: McpDocsPageProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [languageOpen, setLanguageOpen] = useState(false);

  const configSnippet = buildMcpConfigSnippet(API_CONFIG.BASE_URL, MCP_PLACEHOLDER_TOKEN, mcpProduct);

  useEffect(() => {
    ga.event('docs_page_viewed', { page: 'mcp' });
  }, []);

  const copyConfig = async () => {
    await navigator.clipboard.writeText(configSnippet);
    toast.success(t('docs.mcp.configCopied'));
  };

  return (
    <SketchPageShell className="min-h-screen flex flex-col">
      <SeoHead
        meta={MUSIC_SEO.docsMcp}
        jsonLd={techArticleJsonLd({
          title: t('docs.mcp.title'),
          description: t('docs.mcp.intro'),
          path: '/docs/mcp',
        })}
      />
      <SketchNavBar>
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-3">
              <Link
                to="/docs"
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--color-hover)] transition-colors group"
              >
                <ArrowLeftIcon className="h-5 w-5 group-hover:-translate-x-1 transition-transform duration-200" />
                <span className="text-sm font-medium">{t('docs.hub.title')}</span>
              </Link>
            </div>
            <button
              type="button"
              onClick={() => setLanguageOpen(true)}
              className="px-3 py-2 text-sm rounded-lg hover:bg-[var(--color-hover)] transition-colors"
            >
              🌐 {t('menu.language')}
            </button>
          </div>
        </div>
      </SketchNavBar>

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 text-[var(--color-text)]">
        <div className="max-w-3xl mx-auto space-y-8">
          <div>
            <h1 className="text-3xl font-bold mb-3">{t('docs.mcp.title')}</h1>
            <p className="text-[var(--color-muted)] leading-relaxed">{t('docs.mcp.intro')}</p>
            <p className="text-[var(--color-muted)] leading-relaxed mt-3 text-sm">
              {t('docs.mcp.packageSummary')}
            </p>
          </div>

          <section>
            <h2 className="text-lg font-semibold mb-2">{t('docs.mcp.installTitle')}</h2>
            <p className="text-sm text-[var(--color-muted)] mb-3">{t('docs.mcp.installDescription')}</p>
            <p className="text-sm text-[var(--color-muted)] mb-2">{t('docs.mcp.installCommandHint')}</p>
            <pre className="text-xs overflow-x-auto p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] mb-3 font-mono">
              npx -y {MCP_PACKAGE_NAME}
            </pre>
            <p className="text-sm">
              <a href={MCP_NPM_URL} className="text-[var(--brand-from)] hover:underline" target="_blank" rel="noopener noreferrer">
                {t('docs.mcp.installNpmLink')}
              </a>
              <span className="text-[var(--color-muted)] mx-2">·</span>
              <a href={MCP_REPO_URL} className="text-[var(--brand-from)] hover:underline" target="_blank" rel="noopener noreferrer">
                {t('docs.mcp.installRepoLink')}
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">{t('docs.mcp.prerequisitesTitle')}</h2>
            <ul className="list-disc ml-6 space-y-1 text-[var(--color-muted)]">
              <li>{t('docs.mcp.prerequisiteAccount')}</li>
              <li>{t('docs.mcp.prerequisiteToken')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">{t('docs.mcp.tokenTitle')}</h2>
            <ol className="list-decimal ml-6 space-y-1 mb-4">
              <li>{t('docs.mcp.tokenStep1')}</li>
              <li>{t('docs.mcp.tokenStep2')}</li>
              <li>{t('docs.mcp.tokenStep3')}</li>
              <li>{t('docs.mcp.tokenStep4')}</li>
            </ol>
            <Link to={user ? '/settings' : '/auth/login'}>
              <SketchButton variant="primary">
                {user ? t('docs.mcp.tokenSettingsCta') : t('docs.mcp.tokenLoginCta')}
              </SketchButton>
            </Link>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">{t('docs.mcp.configTitle')}</h2>
            <p className="text-sm text-[var(--color-muted)] mb-3">{t('docs.mcp.configDescription')}</p>
            <pre className="text-xs overflow-x-auto p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] mb-3">
              {configSnippet}
            </pre>
            <SketchButton type="button" onClick={copyConfig}>
              {t('docs.mcp.copyConfig')}
            </SketchButton>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">{t('docs.mcp.envTitle')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b border-[var(--color-border)]">
                    <th className="py-2 pr-4">{t('docs.mcp.envName')}</th>
                    <th className="py-2 pr-4">{t('docs.mcp.envRequired')}</th>
                    <th className="py-2">{t('docs.mcp.envDescription')}</th>
                  </tr>
                </thead>
                <tbody>
                  {MCP_ENV_VARS.map((row) => (
                    <tr key={row.name} className="border-b border-[var(--color-border)]/50">
                      <td className="py-2 pr-4 font-mono text-xs">{row.name}</td>
                      <td className="py-2 pr-4">{row.required ? 'Yes' : 'No'}</td>
                      <td className="py-2 text-[var(--color-muted)]">{row.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">{t('docs.mcp.toolsTitle')}</h2>
            <div className="space-y-3">
              <SketchSection title={t('docs.mcp.toolsShared')} defaultOpen tiltSeed="shared">
                <ToolTable tools={MCP_SHARED_TOOLS} nameLabel={t('docs.mcp.toolName')} descLabel={t('docs.mcp.toolDescription')} />
              </SketchSection>
              {showMusicTools && (
                <SketchSection title={t('docs.mcp.toolsMusic')} defaultOpen={false} tiltSeed="music">
                  <ToolTable tools={MCP_MUSIC_TOOLS} nameLabel={t('docs.mcp.toolName')} descLabel={t('docs.mcp.toolDescription')} />
                </SketchSection>
              )}
              {showCreatorTools && (
                <SketchSection title={t('docs.mcp.toolsCreator')} defaultOpen={false} tiltSeed="creator">
                  <ToolTable tools={MCP_CREATOR_TOOLS} nameLabel={t('docs.mcp.toolName')} descLabel={t('docs.mcp.toolDescription')} />
                </SketchSection>
              )}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">{t('docs.mcp.uploadsTitle')}</h2>
            <p className="text-sm text-[var(--color-muted)]">{t('docs.mcp.uploadsBody')}</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">{t('docs.mcp.scopesTitle')}</h2>
            <div className="overflow-x-auto mb-3">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b border-[var(--color-border)]">
                    <th className="py-2 pr-4">{t('docs.mcp.scopeColumn')}</th>
                    <th className="py-2">{t('docs.mcp.accessColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {MCP_SCOPES.map((row) => (
                    <tr key={row.scope} className="border-b border-[var(--color-border)]/50">
                      <td className="py-2 pr-4 font-mono text-xs">{row.scope}</td>
                      <td className="py-2 text-[var(--color-muted)]">{row.access}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-[var(--color-muted)]">{t('docs.mcp.scopesRestricted')}</p>
          </section>

          <section className="border-t border-[var(--color-border)] pt-6">
            <h2 className="text-lg font-semibold mb-2">{t('docs.mcp.ctaTitle')}</h2>
            <p className="text-[var(--color-muted)] mb-4">{t('docs.mcp.ctaBody')}</p>
            <Link to={user ? '/settings' : '/auth/login'}>
              <SketchButton variant="primary">
                {user ? t('docs.mcp.tokenSettingsCta') : t('docs.mcp.tokenLoginCta')}
              </SketchButton>
            </Link>
          </section>
        </div>
      </main>

      <LanguageModal open={languageOpen} onClose={() => setLanguageOpen(false)} />
    </SketchPageShell>
  );
};

export default McpDocsPage;
